/** Pure reducer: `(city, action, now) → city`. Building-scoped actions act on
 *  the active building; they no-op until you own one. */
import type { City, SimAction, Building } from './types';
import { roomDef, canBuildHere, SIM } from './config';
import { stepSim } from './sim';
import { doBuyPlot } from './progress';
import {
  cloneCity,
  activeBuilding,
  findRoom,
  roomAt,
  assignedTo,
  floorCost,
  maxCrewFor,
  pushLog,
} from './world';

export const SECURITY_UPGRADES: Record<string, { name: string; blurb: string; base: number; growth: number; max: number }> = {
  cameras: { name: 'CCTV Network', blurb: '+5 raid defense per level', base: 220, growth: 1.6, max: 5 },
  bribes: { name: 'Dirty Cops', blurb: '+5 safe HEAT threshold per level', base: 350, growth: 1.75, max: 5 },
};

export function securityCost(b: Building, id: string): number {
  const u = SECURITY_UPGRADES[id];
  const lvl = b.security[id] ?? 0;
  return Math.round(u.base * Math.pow(u.growth, lvl));
}

export function reduce(city: City, action: SimAction, _now: number): City {
  switch (action.type) {
    case 'tick': {
      const d = cloneCity(city);
      stepSim(d, action.dt);
      return d;
    }
    case 'setSpeed': {
      if (city.speed === action.speed) return city;
      const d = cloneCity(city);
      d.speed = Math.max(0, Math.min(3, action.speed));
      return d;
    }
    case 'switchBuilding': {
      if (!city.buildings.some((b) => b.id === action.buildingId)) return city;
      const d = cloneCity(city);
      d.activeId = action.buildingId;
      return d;
    }
    case 'buyPlot': {
      const d = cloneCity(city);
      if (!doBuyPlot(d, action.plotId)) return city;
      return d;
    }
    case 'assign': {
      const b = activeBuilding(city);
      if (!b) return city;
      const c = b.colonists.find((x) => x.id === action.colonistId);
      if (!c) return city;
      if (action.roomId != null) {
        const room = findRoom(b, action.roomId);
        if (!room || !roomDef(room.type).workplace) return city;
        const cap = roomDef(room.type).workSlots;
        if (c.assignedRoomId !== room.id && assignedTo(b, room.id).length >= cap) return city;
      }
      const d = cloneCity(city);
      activeBuilding(d)!.colonists.find((x) => x.id === action.colonistId)!.assignedRoomId = action.roomId;
      return d;
    }
    case 'buildRoom': {
      const b = activeBuilding(city);
      if (!b) return city;
      const def = roomDef(action.roomType);
      if (action.floor < b.floorMin || action.floor > b.floorMax) return city;
      if (action.slot < 0 || action.slot >= SIM.SLOTS_PER_FLOOR) return city;
      if (!canBuildHere(action.roomType, action.floor, b.floorMax)) return city;
      if (roomAt(b, action.floor, action.slot)) return city;
      if (city.cash < def.cost) return city;
      const d = cloneCity(city);
      const db = activeBuilding(d)!;
      d.cash -= def.cost;
      db.rooms.push({ id: db.nextRoomId++, type: action.roomType, floor: action.floor, slot: action.slot, tier: 1, damagedUntil: 0, progress: 0 });
      pushLog(d, 'info', `Built a ${def.name} in ${db.name}.`);
      return d;
    }
    case 'addFloor': {
      const b = activeBuilding(city);
      if (!b) return city;
      const cost = floorCost(b);
      if (city.cash < cost) return city;
      const d = cloneCity(city);
      const db = activeBuilding(d)!;
      d.cash -= cost;
      db.floorMax += 1;
      db.maxCrew = maxCrewFor(db);
      pushLog(d, 'info', `${db.name}: built up to floor ${db.floorMax}.`);
      return d;
    }
    case 'digBasement': {
      const b = activeBuilding(city);
      if (!b) return city;
      const cost = floorCost(b);
      if (city.cash < cost) return city;
      const d = cloneCity(city);
      const db = activeBuilding(d)!;
      d.cash -= cost;
      db.floorMin -= 1;
      db.maxCrew = maxCrewFor(db);
      pushLog(d, 'info', `${db.name}: dug basement level ${-db.floorMin}.`);
      return d;
    }
    case 'buySecurity': {
      const b = activeBuilding(city);
      if (!b) return city;
      const u = SECURITY_UPGRADES[action.id];
      if (!u) return city;
      const lvl = b.security[action.id] ?? 0;
      if (lvl >= u.max) return city;
      const cost = securityCost(b, action.id);
      if (city.cash < cost) return city;
      const d = cloneCity(city);
      d.cash -= cost;
      activeBuilding(d)!.security[action.id] = lvl + 1;
      return d;
    }
    case 'toggleLayLow': {
      if (!activeBuilding(city)) return city;
      const d = cloneCity(city);
      const db = activeBuilding(d)!;
      db.layingLow = !db.layingLow;
      return d;
    }
    case 'bribeRaid': {
      const b = activeBuilding(city);
      if (!b || !b.raid || city.cash < b.raid.bribeCost) return city;
      const d = cloneCity(city);
      const db = activeBuilding(d)!;
      d.cash -= db.raid!.bribeCost;
      db.raid = null;
      d.raidsFaced += 1;
      d.raidsSurvived += 1;
      db.heat = Math.min(db.heat, 28);
      pushLog(d, 'good', `💵 You bribed the raid away from ${db.name}.`);
      return d;
    }
    case 'acceptRecruit': {
      const offer = city.recruit;
      const b = activeBuilding(city);
      if (!offer || !b || city.cash < offer.cost) return city;
      if (b.colonists.length >= b.maxCrew) return city; // building full
      const d = cloneCity(city);
      const db = activeBuilding(d)!;
      d.cash -= offer.cost;
      const colonist = { ...d.recruit!.colonist, id: db.nextColonistId++, floor: 0, x: 0.1 };
      colonist.homeBunkId = db.rooms.find((r) => r.type === 'bunk')?.id ?? null;
      db.colonists.push(colonist);
      d.recruit = null;
      pushLog(d, 'good', `${colonist.name} joined ${db.name}.`);
      return d;
    }
    case 'dismissRecruit': {
      if (!city.recruit) return city;
      const d = cloneCity(city);
      d.recruit = null;
      return d;
    }
    case 'repairRoom': {
      const b = activeBuilding(city);
      if (!b) return city;
      const room = findRoom(b, action.roomId);
      if (!room || room.damagedUntil <= city.t || city.cash < 80) return city;
      const d = cloneCity(city);
      d.cash -= 80;
      findRoom(activeBuilding(d)!, action.roomId)!.damagedUntil = 0;
      return d;
    }
    case 'braceRoom': {
      const b = activeBuilding(city);
      if (!b || !b.raid || city.t >= b.raid.resolveAt) return city;
      if (!findRoom(b, action.roomId)) return city;
      const d = cloneCity(city);
      const raid = activeBuilding(d)!.raid!;
      raid.bracedRoomId = raid.bracedRoomId === action.roomId ? null : action.roomId;
      return d;
    }
    case 'dismissToast': {
      if (!city.toasts.some((t) => t.id === action.id)) return city;
      const d = cloneCity(city);
      d.toasts = d.toasts.filter((t) => t.id !== action.id);
      return d;
    }
    default: {
      const _never: never = action;
      void _never;
      return city;
    }
  }
}
